<template>
  <div v-if="plan.isTShirt && apps.status.isLoading" class="loader center-middle">
    <loading-state :size="50" />
  </div>
  <div v-else-if="plan.isTShirt && apps.status.isErrored">
    <error-state />
  </div>
  <form v-else @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />
    <div class="d-flex align-items-center mb-3">
      <Logo :logo="getSelectedAppType()" :size="30" class="mr-2" />
      <div>{{ getSelectedAppName() }}</div>
    </div>
    <FormSelect
      name="app"
      v-model="fields.appId"
      testId="app-input"
      :label="`${$t('global.application')} *`"
      :placeholder="$t('global.application')"
      :errors="validation.fields.appId.errors"
      :dirty="validation.fields.appId.dirty"
      :disabled="isSubmitting"
    >
      <option v-for="option in apps.list" :key="option.id" :value="option.id">
        {{ option.name }}
      </option>
    </FormSelect>
    <FormCheckbox
      v-if="allowAutoInstall"
      v-model="fields.installApp"
      :label="$t('global.auto-install')"
      :disabled="isSubmitting"
      :description="$t('global.auto-install-description', { appType: getSelectedAppName() })"
    />
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-create-site-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="create-site-button"
      >
        {{ $t("global.site-add") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import ErrorState from "@/components/display/ErrorState";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormMessage from "@/components/display/form/FormMessage";
import FormSelect from "@/components/display/form/FormSelect";
import LoadingState from "@/components/display/LoadingState";
import Logo from "@/components/display/Logo";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { addSiteSecondary as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "SiteAddSecondaryForm",
  props: {
    app: {
      type: Object,
      required: true,
    },
    plan: {
      type: Object,
      required: true,
    },
    siteId: Number,
  },
  components: {
    Btn,
    ErrorState,
    FormCheckbox,
    FormMessage,
    FormSelect,
    LoadingState,
    Logo,
  },
  setup(props, { emit, root }) {
    const isSubmitting = ref(false);
    const errors = ref(null);
    const apps = computed(() => store.state.apps);
    const fields = reactive({ appId: props.app.id, installApp: false });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        appId: { required: root.$t("validation.app-required") },
      }
    );

    const cancel = () => emit("close");

    const selectedApp = () => apps.value.list.find(({ id }) => id === Number(fields.appId));
    const getSelectedAppName = () => selectedApp()?.name;
    const getSelectedAppType = () => selectedApp()?.type;

    const isSubmittable = () => {
      return !isSubmitting.value && validation.errors.length === 0;
    };
    const allowAutoInstall = computed(
      () =>
        !["bigcommerce", "generic", "magento", "orocrm"].includes(
          apps.value.list.find(({ id }) => id === Number(fields.appId))?.type
        )
    );
    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          isSubmitting.value = true;
          const installApp = allowAutoInstall.value ? fields.installApp : false;
          store
            .dispatch("sites/createSecondary", {
              id: props.siteId,
              data: {
                appId: fields.appId,
                installApp,
              },
            })
            .then(() => {
              emit("close");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.site-add-error-message"),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };

    onMounted(() => {
      if (props.plan.isTShirt) {
        store.dispatch("apps/getList");
      }
    });

    return {
      errors,
      fields,
      isSubmitting,
      submit,
      validation,
      apps,
      cancel,
      getSelectedAppName,
      getSelectedAppType,
      isSubmittable,
      allowAutoInstall,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
